/* eslint-disable max-len */
import type { AnyObjectSchema } from 'yup';
import {
  deriveDyslexiaScreenerNewStaffTrainingRequired,
  dyslexiaScreenerDocumentUploadRequirementFlags,
  dyslexiaScreenerNewStaffTrainingUiRef,
  dyslexiaScreenerPendingFileUploadRef,
  dyslexiaScreenerUploadedFilesSatisfyDocumentRequirements,
  DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO,
  DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES,
  DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE,
  isDyslexiaScreenerNewStaffTrainingSelectionRequired,
  isTrainingAcknowledgementDatePopulated,
  requiresDyslexiaScreenerTrainingDetails,
} from '../../utilities/dyslexiaScreenerUtilities';
import { DyslexiaScreenerFileListItemModel } from '../DyslexiaScreenerFileListItemModel';
import { DyslexiaScreenerModel } from '../DyslexiaScreenerModel';
import {
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_MUST_BE_UPLOADED,
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_OTHER_TRAINING_PERMISSION,
  DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_TRAINING_PARTICIPANTS,
} from '../propTypes/dyslexiaScreenerDocumentUploadValidationMessages';
import { VALIDATION_MESSAGE } from '../ValidationMessage';
import yup from './CustomValidation/yup-extended';

const isCompletionDatePopulated = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const parseIsoDate = (value: unknown): Date | null => {
  if (!value || typeof value !== 'string') return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Require a real 4-digit year (reject values like 0102 from incomplete "3/4/102").
  if (year < 1000 || year > 2999) return null;
  if (month < 1 || month > 12) return null;
  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;

  const parsedDate = new Date(year, month - 1, day);
  if (Number.isNaN(parsedDate.getTime())) return null;

  const isExactCalendarDate =
    parsedDate.getFullYear() === year &&
    parsedDate.getMonth() + 1 === month &&
    parsedDate.getDate() === day;

  return isExactCalendarDate ? parsedDate : null;
};

/** Accepts empty, or a complete calendar date stored as YYYY-MM-DD. Incomplete typed values (e.g. "3/4", "3/4/102") fail. */
const isValidIsoDateOrEmpty = (value: unknown): boolean => {
  if (!value || (typeof value === 'string' && value.trim() === '')) return true;
  return parseIsoDate(value) !== null;
};

const MAX_DATE = new Date(2999, 11, 31);

const isWithinSelectedSchoolYear = (
  completionDate: Date,
  schoolYear: unknown
): boolean => {
  const endYear =
    typeof schoolYear === 'number'
      ? schoolYear
      : Number.parseInt(String(schoolYear ?? ''), 10);
  if (!endYear || Number.isNaN(endYear)) return true;
  const schoolYearStartDate = new Date(endYear - 1, 6, 1); // Jul 1 of previous year
  const schoolYearEndDate = new Date(endYear, 5, 30); // Jun 30 of schoolYear
  return (
    completionDate >= schoolYearStartDate && completionDate <= schoolYearEndDate
  );
};

/** Optional leading minus so we can show "cannot be negative" instead of a generic format error. */
const INTEGER_STRING_PATTERN = /^-?\d+$/;

const FAILED_OR_AT_RISK_COUNT_LABEL = 'Failed or At Risk Count';
const SCREENER_COUNT_LABEL = 'Screener Count';
const isIntegerCountString = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  const s = String(value).trim();
  if (s === '') return false;
  return INTEGER_STRING_PATTERN.test(s);
};

const isIntegerCountStringOrEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return true;
  }
  return isIntegerCountString(value);
};

const parseCountInt = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  if (s === '' || !INTEGER_STRING_PATTERN.test(s)) return null;
  return parseInt(s, 10);
};

const parseOptionalSiblingCountInt = (value: unknown): number | undefined => {
  const n = parseCountInt(value);
  return n === null ? undefined : n;
};

export const DyslexiaScreenerValidationSchema = yup.object({
  id: yup.string().notRequired(),
  grade1ScreenerCompletionDate: yup
    .string()
    .test(
      'grade1-completion-date-valid',
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      (value) => isValidIsoDateOrEmpty(value)
    )
    .test(
      'grade1-completion-date-max',
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      (value) => {
        const parsed = parseIsoDate(value);
        return !parsed || parsed <= MAX_DATE;
      }
    )
    .test(
      'dyslexia-grade1-completion-date-in-school-year',
      'Screener Completion Date must fall within the selected school year.',
      function validateSchoolYearRange(value) {
        if (!value) return true;
        const parsedDate = parseIsoDate(value);
        if (!parsedDate) return true;
        return isWithinSelectedSchoolYear(parsedDate, this.parent.schoolYear);
      }
    ),
  grade1ScreenerUsed: yup.string().when('grade1ScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    otherwise: yup.string().notRequired(),
  }),
  grade1ParentsNotified: yup.boolean().when('grade1ScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup
      .boolean()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .oneOf([true], VALIDATION_MESSAGE.REQUIRED),
    otherwise: yup.boolean().notRequired(),
  }),
  grade1ScreenerCount: yup.string().when('grade1ScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test(
        'grade1-screener-count-integer',
        VALIDATION_MESSAGE.MUST_BE_NUMBER,
        (value) => isIntegerCountStringOrEmpty(value)
      )
      .test(
        'grade1-screener-count-non-negative',
        VALIDATION_MESSAGE.CANNOT_BE_NEGATIVE,
        function grade1ScreenerCountNonNegative(value) {
          const n = parseCountInt(value);
          if (n === null) return true;
          return n >= 0;
        }
      )
      .test(
        'grade1-screener-count-not-less-than-at-risk',
        VALIDATION_MESSAGE.CANNOT_BE_SMALLER_THAN(
          FAILED_OR_AT_RISK_COUNT_LABEL
        ),
        function grade1ScreenerCountVsAtRisk(value) {
          const n = parseCountInt(value);
          if (n === null || n < 0) return true;
          const atRisk = parseOptionalSiblingCountInt(
            this.parent.grade1ScreenerFailedOrAtRiskCount
          );
          if (atRisk === undefined) return true;
          return n >= atRisk;
        }
      ),
    otherwise: yup.string().notRequired(),
  }),
  grade1ScreenerFailedOrAtRiskCount: yup
    .string()
    .when('grade1ScreenerCompletionDate', {
      is: isCompletionDatePopulated,
      then: yup
        .string()
        .required(VALIDATION_MESSAGE.REQUIRED)
        .test(
          'grade1-at-risk-count-integer',
          VALIDATION_MESSAGE.MUST_BE_NUMBER,
          (value) => isIntegerCountStringOrEmpty(value)
        )
        .test(
          'grade1-at-risk-count-non-negative',
          VALIDATION_MESSAGE.CANNOT_BE_NEGATIVE,
          function grade1AtRiskNonNegative(value) {
            const n = parseCountInt(value);
            if (n === null) return true;
            return n >= 0;
          }
        )
        .test(
          'grade1-at-risk-not-greater-than-screener',
          VALIDATION_MESSAGE.CANNOT_BE_GREATER_THAN(SCREENER_COUNT_LABEL),
          function grade1AtRiskVsScreener(value) {
            const n = parseCountInt(value);
            if (n === null || n < 0) return true;
            const screener = parseOptionalSiblingCountInt(
              this.parent.grade1ScreenerCount
            );
            if (screener === undefined) return true;
            return n <= screener;
          }
        ),
      otherwise: yup.string().notRequired(),
    }),
  kgScreenerCompletionDate: yup
    .string()
    .test(
      'kg-completion-date-valid',
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      (value) => isValidIsoDateOrEmpty(value)
    )
    .test(
      'kg-completion-date-max',
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      (value) => {
        const parsed = parseIsoDate(value);
        return !parsed || parsed <= MAX_DATE;
      }
    )
    .test(
      'dyslexia-kg-completion-date-in-school-year',
      'Screener Completion Date must fall within the selected school year.',
      function validateSchoolYearRange(value) {
        if (!value) return true;
        const parsedDate = parseIsoDate(value);
        if (!parsedDate) return true;
        return isWithinSelectedSchoolYear(parsedDate, this.parent.schoolYear);
      }
    ),
  kgScreenerUsed: yup.string().when('kgScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup.string().required(VALIDATION_MESSAGE.REQUIRED),
    otherwise: yup.string().notRequired(),
  }),
  kgParentsNotified: yup.boolean().when('kgScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup
      .boolean()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .oneOf([true], VALIDATION_MESSAGE.REQUIRED),
    otherwise: yup.boolean().notRequired(),
  }),
  kgScreenerCount: yup.string().when('kgScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test(
        'kg-screener-count-integer',
        VALIDATION_MESSAGE.MUST_BE_NUMBER,
        (value) => isIntegerCountStringOrEmpty(value)
      )
      .test(
        'kg-screener-count-non-negative',
        VALIDATION_MESSAGE.CANNOT_BE_NEGATIVE,
        function kgScreenerCountNonNegative(value) {
          const n = parseCountInt(value);
          if (n === null) return true;
          return n >= 0;
        }
      )
      .test(
        'kg-screener-count-not-less-than-at-risk',
        VALIDATION_MESSAGE.CANNOT_BE_SMALLER_THAN(
          FAILED_OR_AT_RISK_COUNT_LABEL
        ),
        function kgScreenerCountVsAtRisk(value) {
          const n = parseCountInt(value);
          if (n === null || n < 0) return true;
          const atRisk = parseOptionalSiblingCountInt(
            this.parent.kgScreenerFailedOrAtRiskCount
          );
          if (atRisk === undefined) return true;
          return n >= atRisk;
        }
      ),
    otherwise: yup.string().notRequired(),
  }),
  kgScreenerFailedOrAtRiskCount: yup.string().when('kgScreenerCompletionDate', {
    is: isCompletionDatePopulated,
    then: yup
      .string()
      .required(VALIDATION_MESSAGE.REQUIRED)
      .test(
        'kg-at-risk-count-integer',
        VALIDATION_MESSAGE.MUST_BE_NUMBER,
        (value) => isIntegerCountStringOrEmpty(value)
      )
      .test(
        'kg-at-risk-count-non-negative',
        VALIDATION_MESSAGE.CANNOT_BE_NEGATIVE,
        function kgAtRiskNonNegative(value) {
          const n = parseCountInt(value);
          if (n === null) return true;
          return n >= 0;
        }
      )
      .test(
        'kg-at-risk-not-greater-than-screener',
        VALIDATION_MESSAGE.CANNOT_BE_GREATER_THAN(SCREENER_COUNT_LABEL),
        function kgAtRiskVsScreener(value) {
          const n = parseCountInt(value);
          if (n === null || n < 0) return true;
          const screener = parseOptionalSiblingCountInt(
            this.parent.kgScreenerCount
          );
          if (screener === undefined) return true;
          return n <= screener;
        }
      ),
    otherwise: yup.string().notRequired(),
  }),
  trainingAcknowledgementDate: yup
    .string()
    .test(
      'training-acknowledgement-date-valid',
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      (value) => isValidIsoDateOrEmpty(value)
    )
    .test(
      'training-acknowledgement-date-max',
      VALIDATION_MESSAGE.MUST_BE_VALID_DATE,
      (value) => {
        const parsed = parseIsoDate(value);
        return !parsed || parsed <= MAX_DATE;
      }
    )
    .test(
      'dyslexia-training-acknowledgement-date-in-school-year',
      'Training Acknowledgement Date must fall within the selected school year.',
      function validateSchoolYearRange(value) {
        if (!value) return true;
        const parsedDate = parseIsoDate(value);
        if (!parsedDate) return true;
        return isWithinSelectedSchoolYear(parsedDate, this.parent.schoolYear);
      }
    )
    .test(
      'dyslexia-new-staff-training-required',
      VALIDATION_MESSAGE.REQUIRED,
      function validateNewStaffTrainingRequired() {
        if (!isDyslexiaScreenerNewStaffTrainingSelectionRequired(this.parent)) {
          return true;
        }
        const fromRef =
          dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired;
        if (
          fromRef === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES ||
          fromRef === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO
        ) {
          return true;
        }
        /** Ref updates in a child useEffect after Formik commits the date; derive matches that sync. */
        const derived = deriveDyslexiaScreenerNewStaffTrainingRequired(
          this.parent.schoolYear,
          this.parent.trainingAcknowledgementDate ?? '',
          this.parent
        );
        return (
          derived === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES ||
          derived === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO
        );
      }
    ),
  trainingUsed: yup
    .string()
    .test(
      'dyslexia-training-used-required',
      VALIDATION_MESSAGE.REQUIRED,
      function validateTrainingUsed(value) {
        if (!requiresDyslexiaScreenerTrainingDetails(this.parent)) {
          return true;
        }
        return Boolean(value?.trim());
      }
    ),
  otherTrainingUsed: yup
    .string()
    .max(400, VALIDATION_MESSAGE.AT_MOST_CHARS(400))
    .test(
      'dyslexia-other-training-used-required',
      VALIDATION_MESSAGE.REQUIRED,
      function validateOtherTrainingUsed(value) {
        if (!requiresDyslexiaScreenerTrainingDetails(this.parent)) {
          return true;
        }
        const trainingUsed = (
          this.parent.trainingUsed as string | undefined
        )?.trim();
        if (trainingUsed !== DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE) {
          return true;
        }
        return Boolean(value?.trim());
      }
    ),
  otherTrainingAcknowldegment: yup
    .boolean()
    .test(
      'dyslexia-training-acknowledgment-required',
      VALIDATION_MESSAGE.REQUIRED,
      function validateTrainingAcknowledgment(value) {
        if (
          !isTrainingAcknowledgementDatePopulated(
            this.parent.trainingAcknowledgementDate ?? ''
          )
        ) {
          return true;
        }
        return value === true;
      }
    ),
  dyslexiaScreenerDocumentUploadInProgress: yup
    .boolean()
    .notRequired()
    .test(
      'dyslexia-document-upload-in-progress',
      'Please complete file upload before saving.',
      () => !dyslexiaScreenerPendingFileUploadRef.hasPendingFile
    ),
});

const dyslexiaScreenerDocumentUploadMissingForParent = (
  parent: DyslexiaScreenerModel | null | undefined,
  uploadedFiles: readonly DyslexiaScreenerFileListItemModel[]
): {
  missingTrainingParticipants: boolean;
  missingOtherPermission: boolean;
} | null => {
  if (!parent || !(parent.id ?? '').trim()) {
    return null;
  }
  const flags = dyslexiaScreenerDocumentUploadRequirementFlags({
    schoolYear: parent.schoolYear,
    trainingAcknowledgementDate: parent.trainingAcknowledgementDate ?? '',
    trainingUsed: parent.trainingUsed ?? '',
  });
  return dyslexiaScreenerUploadedFilesSatisfyDocumentRequirements(
    uploadedFiles,
    flags
  );
};

/**
 * Extends {@link DyslexiaScreenerValidationSchema} with required-document rules (US 29032).
 * Uses uploaded file list from Redux; new-staff rules read {@link dyslexiaScreenerNewStaffTrainingUiRef} at validate time.
 */
export const buildDyslexiaScreenerValidationSchemaWithDocumentUploads =
  (options: {
    uploadedFiles: DyslexiaScreenerFileListItemModel[];
  }): AnyObjectSchema => {
    const { uploadedFiles } = options;

    return DyslexiaScreenerValidationSchema.test(
      'dyslexia-required-document-specific',
      '',
      function validateRequiredDocumentMessage(value) {
        const missing = dyslexiaScreenerDocumentUploadMissingForParent(
          value as unknown as DyslexiaScreenerModel,
          uploadedFiles
        );
        if (!missing) {
          return true;
        }
        if (missing.missingOtherPermission) {
          return this.createError({
            path: 'dyslexiaScreenerDocumentUploadRequirement',
            message:
              DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_OTHER_TRAINING_PERMISSION,
          });
        }
        if (missing.missingTrainingParticipants) {
          return this.createError({
            path: 'dyslexiaScreenerDocumentUploadRequirement',
            message:
              DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_TRAINING_PARTICIPANTS,
          });
        }
        return true;
      }
    )
      .test(
        'dyslexia-required-document-upload',
        '',
        function validateDocumentMustBeUploaded(value) {
          if (dyslexiaScreenerPendingFileUploadRef.hasPendingFile) {
            return true;
          }
          const missing = dyslexiaScreenerDocumentUploadMissingForParent(
            value as unknown as DyslexiaScreenerModel,
            uploadedFiles
          );
          if (
            !missing ||
            (!missing.missingOtherPermission &&
              !missing.missingTrainingParticipants)
          ) {
            return true;
          }
          return this.createError({
            path: 'dyslexiaScreenerDocumentUpload',
            message: DYSLEXIA_SCREENER_DOCUMENT_UPLOAD_ERROR_MUST_BE_UPLOADED,
          });
        }
      )
      .test(
        'dyslexia-pending-file-upload',
        'Please complete file upload before saving.',
        () => !dyslexiaScreenerPendingFileUploadRef.hasPendingFile
      );
  };
